import type { Unit } from '$lib/types/unit';
import type { ZoneResponse } from '$lib/types/zone';
import type { RoomResponse } from '$lib/types/room';
import type { LoadResponse } from '$lib/types/load';
import type { InputDeviceResponse } from '$lib/types/input_device';
import type { InputKeyResponse } from '$lib/types/input_key';
import type { SceneResponse } from '$lib/types/scene';
import type { SceneLoadResponse } from '$lib/types/scene_load';
import type { RCUResponse } from '$lib/types/rcu';
import type { DinModuleResponse } from '$lib/types/din_module';
import type { ProductResponse } from '$lib/types/product';

type ChannelRow = { load: number | null; object_id: number };

export type WorkflowIssue = {
  id: string;
  severity: string;
  entity_type: string;
  entity_id: number | null;
  label: string;
  message: string;
  section: string;
  action_label: string;
  count?: number;
};

export type UnitWorkflowData = {
  unit: Unit | null;
  zones?: ZoneResponse[];
  rooms?: RoomResponse[];
  loads?: LoadResponse[];
  inputDevices?: InputDeviceResponse[];
  inputKeys?: InputKeyResponse[];
  sceneLoads?: SceneLoadResponse[];
  products?: ProductResponse[];
  rcus?: RCUResponse[];
  dinModules?: DinModuleResponse[];
  scenes?: SceneResponse[];
  channels?: ChannelRow[];
};

function stageStatus(isComplete: boolean, issueCount: number): 'attention' | 'complete' | 'empty' {
  if (issueCount > 0) return 'attention';
  if (isComplete) return 'complete';
  return 'empty';
}

function duplicateAddressGroups(devices: InputDeviceResponse[]) {
  const counts = new Map<number, number>();
  for (const device of devices) {
    const address = device.keys[0]?.address;
    if (address == null) continue;
    counts.set(address, (counts.get(address) || 0) + 1);
  }
  return [...counts.entries()]
    .filter(([, count]) => count > 1)
    .map(([address, count]) => ({ address, count }));
}

function devicesWithoutActions(devices: InputDeviceResponse[], inputKeys: InputKeyResponse[]) {
  const deviceIds = new Set(devices.map((d) => d.id));
  const keyIdsWithActions = new Set<number>();
  for (const key of inputKeys) {
    if (key.actions && key.actions.length > 0) {
      keyIdsWithActions.add(key.id);
    }
  }
  const keysByDevice = new Map<number, InputKeyResponse[]>();
  for (const key of inputKeys) {
    if (!deviceIds.has(key.device)) continue;
    if (!keysByDevice.has(key.device)) keysByDevice.set(key.device, []);
    keysByDevice.get(key.device)!.push(key);
  }
  return devices.filter((device) => {
    const keys = keysByDevice.get(device.id) || [];
    return keys.length === 0 || keys.every((key) => !keyIdsWithActions.has(key.id));
  });
}

function scenesWithoutLoads(scenes: SceneResponse[], sceneLoads: SceneLoadResponse[]) {
  const sceneIdsWithLoads = new Set(sceneLoads.map((sceneLoad) => sceneLoad.scene));
  return scenes.filter((scene) => !sceneIdsWithLoads.has(scene.id));
}

function channelsForUnit(
  unitLoads: LoadResponse[],
  unitRcus: RCUResponse[],
  unitDinModules: DinModuleResponse[],
  channels: ChannelRow[]
) {
  const loadIds = new Set(unitLoads.map((load) => load.id));
  const controllerIds = new Set([
    ...unitRcus.map((rcu) => rcu.id),
    ...unitDinModules.map((module) => module.id)
  ]);
  return channels.filter(
    (channel) =>
      (channel.load != null && loadIds.has(channel.load)) || controllerIds.has(channel.object_id)
  );
}

export function computeUnitWorkflow(data: UnitWorkflowData) {
  const unit = data.unit;
  const zones = data.zones || [];
  const rooms = data.rooms || [];
  const loads = data.loads || [];
  const inputDevices = data.inputDevices || [];
  const inputKeys = data.inputKeys || [];
  const sceneLoads = data.sceneLoads || [];
  const products = data.products || [];
  const rcus = unit ? (data.rcus || []).filter((rcu) => rcu.unit === unit.id) : [];
  const rcuIds = new Set(rcus.map((rcu) => rcu.id));
  const dinModules = (data.dinModules || []).filter((module) => rcuIds.has(module.rcu));
  const scenes = unit ? (data.scenes || []).filter((scene) => scene.unit === unit.id) : [];
  const channels = channelsForUnit(loads, rcus, dinModules, data.channels || []);
  const mappedLoadIds = new Set(channels.map((channel) => channel.load));
  const unassignedLoads = loads.filter((load) => !mappedLoadIds.has(load.id));
  const duplicateAddresses = duplicateAddressGroups(inputDevices);
  const unprogrammedDevices = devicesWithoutActions(inputDevices, inputKeys);
  const emptyScenes = scenesWithoutLoads(scenes, sceneLoads);
  const productCatalogReady = products.length > 0;

  const issues = [
    rooms.length === 0
      ? {
          id: 'missing-rooms',
          severity: 'warning',
          entity_type: 'room',
          entity_id: null,
          label: 'No rooms yet',
          message: 'Create the physical room structure before adding loads.',
          section: 'zones',
          action_label: 'Create rooms'
        }
      : null,
    loads.length === 0
      ? {
          id: 'missing-loads',
          severity: 'warning',
          entity_type: 'load',
          entity_id: null,
          label: 'No loads defined',
          message: 'Add lighting, curtain, relay, or DALI loads per room.',
          section: 'loads',
          action_label: 'Add loads'
        }
      : null,
    unassignedLoads.length > 0
      ? {
          id: 'unassigned-loads',
          severity: 'error',
          entity_type: 'load',
          entity_id: unassignedLoads[0]?.id ?? null,
          label: `${unassignedLoads.length} unassigned loads`,
          message: 'Assign each load to an RCU or DIN channel.',
          section: 'hardware',
          action_label: 'Assign channels',
          count: unassignedLoads.length
        }
      : null,
    duplicateAddresses.length > 0
      ? {
          id: 'duplicate-device-addresses',
          severity: 'error',
          entity_type: 'input_device',
          entity_id: null,
          label: `${duplicateAddresses.length} duplicate device address groups`,
          message: 'Resolve duplicate device addresses before commissioning.',
          section: 'controls',
          action_label: 'Fix addresses',
          count: duplicateAddresses.length
        }
      : null,
    unprogrammedDevices.length > 0
      ? {
          id: 'unprogrammed-devices',
          severity: 'warning',
          entity_type: 'input_device',
          entity_id: unprogrammedDevices[0]?.id ?? null,
          label: `${unprogrammedDevices.length} devices without actions`,
          message: 'Program key actions so every installed control has behavior.',
          section: 'controls',
          action_label: 'Program devices',
          count: unprogrammedDevices.length
        }
      : null,
    emptyScenes.length > 0
      ? {
          id: 'empty-scenes',
          severity: 'warning',
          entity_type: 'scene',
          entity_id: emptyScenes[0]?.id ?? null,
          label: `${emptyScenes.length} scenes without loads`,
          message: 'Add scene load values or remove unused scenes.',
          section: 'controls',
          action_label: 'Review scenes',
          count: emptyScenes.length
        }
      : null
  ].filter((issue) => issue !== null) as WorkflowIssue[];

  const issueCountBySection = issues.reduce<Record<string, number>>((acc, issue) => {
    acc[issue.section] = (acc[issue.section] || 0) + (issue.count || 1);
    return acc;
  }, {});

  const controlIssueCount =
    duplicateAddresses.length + unprogrammedDevices.length + emptyScenes.length;
  const exportIssueCount = unassignedLoads.length + controlIssueCount;

  const stages = [
    {
      id: 'spaces',
      label: 'Spaces',
      detail: `${zones.length} zones, ${rooms.length} rooms`,
      section: 'zones',
      status: stageStatus(zones.length > 0 && rooms.length > 0, 0),
      issue: rooms.length === 0 ? 'Add rooms before defining loads' : '',
      count: rooms.length,
      issueCount: issueCountBySection.zones || 0
    },
    {
      id: 'loads',
      label: 'Loads',
      detail: `${loads.length} loads defined`,
      section: 'loads',
      status: stageStatus(loads.length > 0, 0),
      issue: loads.length === 0 ? 'Define electrical loads per room' : '',
      count: loads.length,
      issueCount: issueCountBySection.loads || 0
    },
    {
      id: 'hardware',
      label: 'Hardware',
      detail: `${rcus.length} RCUs, ${dinModules.length} DIN modules`,
      section: 'hardware',
      status: stageStatus(rcus.length > 0 && unassignedLoads.length === 0, unassignedLoads.length),
      issue: unassignedLoads.length ? `${unassignedLoads.length} loads need channels` : '',
      count: rcus.length + dinModules.length,
      issueCount: issueCountBySection.hardware || 0
    },
    {
      id: 'controls',
      label: 'Controls',
      detail: `${inputDevices.length} devices, ${scenes.length} scenes`,
      section: 'controls',
      status: stageStatus(inputDevices.length > 0 && scenes.length > 0, controlIssueCount),
      issue: controlIssueCount ? `${controlIssueCount} control issues` : '',
      count: inputDevices.length + scenes.length,
      issueCount: issueCountBySection.controls || 0
    },
    {
      id: 'products',
      label: 'Products',
      detail: productCatalogReady
        ? `${products.length} catalog items loaded`
        : 'Catalog not loaded',
      section: 'products',
      status: stageStatus(productCatalogReady, 0),
      issue: productCatalogReady ? '' : 'Load product catalog for BOM work',
      count: products.length,
      issueCount: issueCountBySection.products || 0
    },
    {
      id: 'export',
      label: 'Export',
      detail: 'Firmware and reports',
      section: 'export',
      status: stageStatus(
        rooms.length > 0 && loads.length > 0 && rcus.length > 0 && scenes.length > 0,
        exportIssueCount
      ),
      issue: exportIssueCount ? 'Resolve warnings before export' : '',
      count: 0,
      issueCount: exportIssueCount
    }
  ];

  return {
    unit,
    stages,
    issues,
    issueCountBySection,
    completeCount: stages.filter((stage) => stage.status === 'complete').length,
    attentionCount: issues.length,
    nextAction: issues[0] || {
      id: 'export-ready',
      label: 'Ready for export review',
      message: 'Run reports and firmware export when commissioning data is final.',
      section: 'export',
      action_label: 'Review exports'
    },
    entities: {
      rcus,
      dinModules,
      scenes,
      channels,
      unassignedLoads,
      duplicateAddresses,
      unprogrammedDevices,
      emptyScenes
    }
  };
}
